import { PLAYABLE_STATES, rulesForState } from '../sim/stateRules';
import React, { useEffect, useState } from 'react';
import { useGameStore } from '../store/gameStore';
import { DIFFICULTY_PRESTIGE, Difficulty, stateSchool } from '../sim/league';
import { SCENARIOS, ScenarioId } from '../data/scenarios';
import { SchoolSearch } from './SchoolSearch';
import { MusicTapHint } from './MusicTapHint';
import { HallOfFameArcade } from './HallOfFameBoard';
import { Sheet } from './ui/Sheet';
import { useScrollTopOnScreenChange } from '../utils/pageScroll';
import { CAREER_LENGTHS, CareerLength } from '../sim/careerScore';

// The classic game (a random school at a difficulty, unranked) is hidden for now; flip this to bring it back
const SHOW_CLASSIC_GAME = false;
import { deleteAnySave, listAllSaves, loadAnySave, SaveEntry, signInAvailable, useAccount } from '../services/account';
import { loadAllPlayedGames } from '../services/playedGames';
import { gamesSince, PlayedGame } from '../sim/gameLock';
import { reopenCookieBanner } from '../services/consent';
import { setEntrySource, track } from '../services/analytics';
import TEAM_PAGES from '../data/teamPages.json';
import { parseTeamLink } from '../sim/teamLink';
import { AccountSheet, SignInResult, SignInSheet } from './AccountPanels';
import { version } from '../../package.json';
import { assetUrl, sitePage } from '../utils/site';

const DIFFICULTIES: { id: Difficulty; label: string; blurb: string; color: string }[] = [
  { id: 'EASY', label: 'Easy', blurb: 'Take over a powerhouse: deep talent, rich boosters, title expectations.', color: '#15803D' },
  { id: 'MEDIUM', label: 'Medium', blurb: 'A solid contender. Good players, but you have to win the big games.', color: '#2563EB' },
  { id: 'HARD', label: 'Hard', blurb: 'An underdog program. Build it from the ground up.', color: '#DC2626' }
];

const prestigeRange = (d: Difficulty) => {
  const { min, max } = DIFFICULTY_PRESTIGE[d];
  return max >= 100 ? `${min}+` : `${min}–${max}`;
};

/** Title screen: start a scenario career (or a classic game at a difficulty), continue, or load a saved game. */
export const SplashScreen: React.FC<{ onEnterGame: () => void; canContinue: boolean }> = ({ onEnterGame, canContinue }) => {
  const { newGame, newScenarioGame, loadGame } = useGameStore();
  const [view, setView] = useState<'MENU' | 'NEW' | 'LOAD'>('MENU');
  useScrollTopOnScreenChange(view);
  const [saves, setSaves] = useState<SaveEntry[] | null>(null);
  const account = useAccount();
  const [accountSheet, setAccountSheet] = useState<'SIGN_IN' | 'ACCOUNT' | null>(null);
  // The Hall of Fame, open from the title screen with or without a game loaded (view only)
  const [hallOfFame, setHallOfFame] = useState<CareerLength | null>(null);
  const career = useGameStore((s) => s.career);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The newest save (autosave or manual): Continue picks up there after a refresh or a new visit
  const [latest, setLatest] = useState<SaveEntry | null>(null);
  // Signed in, the lists include the account's cloud saves (the newer copy of each slot)
  const accountKey = `${account.ready}|${account.user?.id ?? ''}`;

  useEffect(() => {
    if (view !== 'MENU' || canContinue) return;
    listAllSaves()
      .then((list) => setLatest(list[0] ?? null))
      .catch(() => setLatest(null));
  }, [view, canContinue, accountKey]);

  // Games already played from a save's point on: loading it won't replay them (a game counts once it kicks off)
  const [played, setPlayed] = useState<PlayedGame[]>([]);
  useEffect(() => {
    if (view !== 'LOAD') return;
    listAllSaves()
      .then(setSaves)
      .catch(() => setSaves([]));
    void loadAllPlayedGames().then(setPlayed);
  }, [view, accountKey]);
  const lockedNote = (save: SaveEntry) => {
    if (!save.careerKey) return null;
    const games = gamesSince(played, save.careerKey, save.currentYear, save.currentWeek);
    if (games.length === 0) return null;
    return games.length === 1 ? `🔒 ${games[0].year === save.currentYear ? '' : `${games[0].year} `}Week ${games[0].week} already played: the result stands` : `🔒 ${games.length} games already played: results stand`;
  };

  const [state, setState] = useState('Texas');
  const [pick, setPick] = useState<{ scenario: ScenarioId; school: string } | null>(null);
  const [coachName, setCoachName] = useState('');
  const [nameMissing, setNameMissing] = useState(false); // Start was tapped without a coach name
  const [length, setLength] = useState<CareerLength>(5);
  const [showClassic, setShowClassic] = useState(false);
  // Pick any program: search every school in the state instead of the featured programs
  const [anyProgram, setAnyProgram] = useState(false);
  // A link from a team page (/?state=texas&school=duncanville) opens New Game with that school and 10 seasons picked,
  // ready for the coach's name; the address is cleaned up so a refresh shows the title screen
  const [teamLink] = useState(() => parseTeamLink(window.location.search));
  useEffect(() => {
    if (!teamLink) return;
    setEntrySource(teamLink.source, teamLink.variant);
    setView('NEW');
    setState(teamLink.state);
    setPick({ scenario: teamLink.scenario, school: teamLink.school });
    setAnyProgram(teamLink.scenario === 'OPEN');
    setLength(10);
    window.history.replaceState(null, '', window.location.pathname);
    const focus = setTimeout(() => document.getElementById('coach-name')?.focus(), 250);
    return () => clearTimeout(focus);
  }, [teamLink]);
  const chooseState = (st: string) => {
    setState(st);
    setPick(null);
  };
  const pickedProgram = pick && SCENARIOS.flatMap((s) => s.programs).find((p) => p.state === state && p.school === pick.school);
  const pickedName = pick ? (pickedProgram?.displayName ?? pick.school) : null;
  const startCareer = () => {
    if (!pick) return;
    // The coach's name is required: it goes on the team, the awards and the Hall of Fame
    if (!coachName.trim()) {
      setNameMissing(true);
      document.getElementById('coach-name')?.focus();
      return;
    }
    setBusy(true);
    setTimeout(() => {
      newScenarioGame(pick.scenario, state, pick.school, coachName, length);
      setBusy(false);
      onEnterGame();
    }, 20);
  };
  const startNew = (difficulty: Difficulty) => {
    setBusy(true);
    // Let the button state paint before building the 254-team world
    setTimeout(() => {
      newGame(difficulty, state);
      setBusy(false);
      onEnterGame();
    }, 20);
  };

  const load = async (entry: SaveEntry) => {
    setBusy(true);
    setError(null);
    try {
      const save = await loadAnySave(entry);
      if (!save) throw new Error('missing');
      loadGame(save);
      onEnterGame();
    } catch {
      setError('That save could not be loaded.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    track('save_deleted', { saves: saves?.length ?? 0 });
    await deleteAnySave(id);
    setConfirmDelete(null);
    setSaves((list) => list?.filter((s) => s.id !== id) ?? null);
  };
  // The title menu's choice (Continue: the game in memory, or the newest save)
  const menuChoice = (choice: 'continue' | 'new' | 'load' | 'hall_of_fame') => track('menu_choice', { choice, ...(choice === 'continue' && { from_save: !canContinue }) });

  return (
    <div className="ui-on-dark" style={pageStyle}>
      {/* The optional account: sign in, or who is signed in */}
      {account.user ? (
        <button className="account-pill" onClick={() => setAccountSheet('ACCOUNT')} aria-label="Account">
          <span className="account-pill-dot" aria-hidden="true">
            ●
          </span>
          {account.user.name || account.user.email?.split('@')[0] || 'Account'}
        </button>
      ) : (
        signInAvailable(account.options) && (
          <button className="account-pill" onClick={() => setAccountSheet('SIGN_IN')}>
            Sign in
          </button>
        )
      )}
      {accountSheet === 'SIGN_IN' && <SignInSheet onClose={() => setAccountSheet(null)} />}
      {accountSheet === 'ACCOUNT' && <AccountSheet onClose={() => setAccountSheet(null)} onSignIn={() => setAccountSheet('SIGN_IN')} />}
      {hallOfFame && (
        <Sheet title="🏆 Hall of Fame" onClose={() => setHallOfFame(null)}>
          <HallOfFameArcade career={career} length={hallOfFame} onLength={setHallOfFame} />
        </Sheet>
      )}
      <div style={{ width: '100%', maxWidth: '760px' }}>
        <div style={{ textAlign: 'center', marginBottom: view === 'MENU' ? '28px' : '16px' }}>
          {/* The logo, under the lights; smaller on New Game and Load Game so their choices stay near the top */}
          <h1 className={view === 'MENU' ? 'title-logo' : 'title-logo title-logo-compact'}>
            <img src={assetUrl('logo/football-coach-logo.webp')} alt="Football Coach High School Edition" width={840} height={459} />
          </h1>
          {view === 'MENU' && (
            <>
              {/* What the game is, for anyone arriving from a link (the same words are in index.html for search engines) */}
              <p className="title-pitch">The high school football coaching sim. Take over a program and build it into a dynasty. Can you win a national title?</p>
              {/* A feature strip, not buttons: no box or shadow, so only the menu below looks tappable */}
              <ul className="title-features" aria-label="What you do">
                {[
                  ['🏈', 'Call the plays'],
                  ['🎓', 'Recruit and develop'],
                  ['🏆', 'Chase state titles']
                ].map(([icon, label]) => (
                  <li key={label}>
                    <span className="title-feature-icon" aria-hidden="true">
                      {icon}
                    </span>
                    <span className="title-feature-label">{label}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {view === 'MENU' && <SignInResult />}
        {view === 'MENU' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '360px', margin: '0 auto' }}>
            {/* Continue: the game in progress, or (after a refresh or a new visit) the newest save */}
            {(canContinue || latest) && (
              <button onClick={() => (menuChoice('continue'), canContinue ? onEnterGame() : latest && load(latest))} disabled={busy} style={menuBtn('#F59E0B', '#0F172A')}>
                ▶️ {busy ? 'Loading…' : 'Continue'}
                {!canContinue && latest && (
                  <span style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginTop: '2px' }}>
                    {latest.teamName} · {latest.currentYear} Wk {latest.currentWeek} · {latest.wins}-{latest.losses}
                    {latest.isAutosave ? ' · Autosave' : ''}
                  </span>
                )}
              </button>
            )}
            {error && <div style={{ color: '#FCA5A5', fontSize: '13px', textAlign: 'center' }}>{error}</div>}
            <button onClick={() => (menuChoice('new'), setView('NEW'))} style={menuBtn('#15803D', '#fff')}>
              🆕 New Game
            </button>
            <button onClick={() => (menuChoice('load'), setView('LOAD'))} style={menuBtn('#334155', '#fff')}>
              📂 Load Game
            </button>
            <button onClick={() => (menuChoice('hall_of_fame'), setHallOfFame(career?.length ?? 3))} style={{ ...menuBtn('#05060F', '#FACC15'), border: '2px solid #FACC15' }}>
              🏆 Hall of Fame
            </button>
            {account.user && account.options.cloudSaves ? (
              <div style={{ textAlign: 'center', fontSize: '12px', color: '#86EFAC' }}>☁️ Saves sync to your account</div>
            ) : (
              signInAvailable(account.options) && <div style={{ textAlign: 'center', fontSize: '12px', color: '#94A3B8' }}>Sign in to keep your saves on every device</div>
            )}
            <MusicTapHint />
          </div>
        )}

        {view === 'NEW' && (
          <div>
            <h2 style={sectionTitle}>Choose a State</h2>
            <div aria-label="State" className="title-states">
              {PLAYABLE_STATES.map((st) => (
                <button key={st} className="ui-chip" aria-pressed={state === st} onClick={() => chooseState(st)} style={{ justifyContent: 'center' }}>
                  {st}
                </button>
              ))}
            </div>
            <p style={{ textAlign: 'center', color: '#94A3B8', fontSize: '13px', margin: '0 0 16px 0' }}>
              {rulesForState(state).governingBody} {/^\d+A$/.test(rulesForState(state).classification) ? 'Class ' : ''}{rulesForState(state).classification} · title game at {rulesForState(state).playoffs.championshipVenue}
            </p>
            <button
              onClick={() => {
                setAnyProgram(!anyProgram);
                setPick(null);
              }}
              aria-expanded={anyProgram}
              disabled={busy}
              style={anyProgramBtn(anyProgram)}
            >
              <span aria-hidden="true" style={{ fontSize: '22px' }}>
                🔍
              </span>
              <span style={{ minWidth: 0 }}>
                <b style={{ display: 'block', fontSize: '15px' }}>Pick any program</b>
                <span style={{ fontSize: '12px', color: '#CBD5E1' }}>Search for a program in {state} and see if you can take it to the title.</span>
              </span>
              <span aria-hidden="true" style={{ marginLeft: 'auto', color: '#93C5FD', fontWeight: 800 }}>
                {anyProgram ? '▾' : '›'}
              </span>
            </button>
            {anyProgram ? (
              <div style={{ marginBottom: '18px' }}>
                <SchoolSearch state={state} initialQuery={teamLink?.scenario === 'OPEN' && teamLink.state === state ? teamLink.school : undefined} selected={pick?.scenario === 'OPEN' ? pick.school : null} onSelect={(s) => setPick({ scenario: 'OPEN', school: s.name })} disabled={busy} />
                <div style={{ textAlign: 'center' }}>
                  <button
                    onClick={() => {
                      setAnyProgram(false);
                      setPick(null);
                    }}
                    style={{ background: 'none', border: 'none', color: '#93C5FD', fontWeight: 700, fontSize: '13px', cursor: 'pointer', padding: '8px 0', minHeight: '40px' }}
                  >
                    ← Back to featured programs
                  </button>
                </div>
              </div>
            ) : (
              <>
              <h2 style={{ ...sectionTitle, marginTop: '16px' }}>Choose Your Program</h2>
              {SCENARIOS.map((scenario) => (
                <section key={scenario.id} aria-label={scenario.title} style={{ marginBottom: '18px' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    <h3 style={{ margin: 0, color: scenario.id === 'RECLAIM' ? '#FCA5A5' : '#FCD34D', fontSize: '17px' }}>{scenario.title}</h3>
                    <span style={{ color: '#94A3B8', fontSize: '13px' }}>{scenario.tagline}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '10px' }}>
                    {scenario.programs
                      .filter((p) => p.state === state)
                      .map((program) => {
                        const school = stateSchool(state, program.school);
                        const selected = pick?.scenario === scenario.id && pick.school === program.school;
                        return (
                          <button
                            key={program.school}
                            aria-pressed={selected}
                            onClick={() => setPick({ scenario: scenario.id, school: program.school })}
                            disabled={busy}
                            style={{ ...cardBtn(school?.primaryColor ?? '#334155', busy), outline: selected ? '3px solid #F59E0B' : 'none', outlineOffset: '2px' }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'baseline' }}>
                              <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#0F172A' }}>{program.displayName}</span>
                              <span style={{ ...badge(scenario.id === 'RECLAIM' ? '#FEE2E2' : '#FEF3C7', scenario.id === 'RECLAIM' ? '#991B1B' : '#92400E'), whiteSpace: 'nowrap' }}>
                                Prestige {scenario.startingPrestige(school?.prestige ?? 0)}
                              </span>
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748B', margin: '2px 0 8px 0' }}>
                              {school?.mascot}
                              {school && ` · ${school.district}`}
                            </div>
                            <div style={{ fontSize: '13px', color: '#334155', lineHeight: 1.45 }}>{program.legacy}</div>
                          </button>
                        );
                      })}
                  </div>
                </section>
              ))}
              </>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '420px', margin: '0 auto' }}>
              <div style={{ color: '#CBD5E1', fontSize: '13px' }}>
                Game length
                <div className="ui-chips" role="group" aria-label="Game length" style={{ marginTop: '4px' }}>
                  {CAREER_LENGTHS.map((years) => (
                    <button key={years} className="ui-chip" aria-pressed={length === years} onClick={() => setLength(years)} style={{ flex: 1, justifyContent: 'center' }}>
                      {years} years
                    </button>
                  ))}
                </div>
              </div>
              <label style={{ color: '#CBD5E1', fontSize: '13px' }}>
                Coach name{' '}
                <span aria-hidden="true" style={{ color: '#FCA5A5' }}>
                  *
                </span>
                <input
                  id="coach-name"
                  value={coachName}
                  onChange={(e) => {
                    setCoachName(e.target.value.slice(0, 24));
                    if (e.target.value.trim()) setNameMissing(false);
                  }}
                  placeholder="Your name (required)"
                  required
                  aria-invalid={nameMissing}
                  aria-describedby={nameMissing ? 'coach-name-error' : undefined}
                  style={{
                    display: 'block',
                    width: '100%',
                    boxSizing: 'border-box',
                    marginTop: '4px',
                    padding: '12px',
                    borderRadius: '8px',
                    border: nameMissing ? '2px solid #F87171' : '1px solid #475569',
                    fontSize: '16px'
                  }}
                />
              </label>
              {nameMissing && (
                <div id="coach-name-error" role="alert" style={{ color: '#FCA5A5', fontSize: '13px', marginTop: '-4px' }}>
                  Enter your coach name to start.
                </div>
              )}
              <button
                onClick={startCareer}
                disabled={!pick || busy}
                style={{ ...menuBtn('#15803D', '#fff'), opacity: !pick || busy ? 0.5 : 1, cursor: !pick ? 'not-allowed' : busy ? 'wait' : 'pointer' }}
              >
                {pickedName ? `Start at ${pickedName}` : 'Pick a program'}
              </button>
            </div>
            {busy && <p style={{ textAlign: 'center', color: '#CBD5E1', marginTop: '16px' }}>Building the league…</p>}
            {SHOW_CLASSIC_GAME && (
              <div style={{ textAlign: 'center', marginTop: '18px' }}>
                <button
                  onClick={() => setShowClassic((v) => !v)}
                  aria-expanded={showClassic}
                  style={{ background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }}
                >
                  {showClassic ? 'Hide classic game' : 'Classic game: a random school (not ranked)'}
                </button>
              </div>
            )}
            {SHOW_CLASSIC_GAME && showClassic && (
              <div style={{ marginTop: '10px' }}>
                <p style={{ textAlign: 'center', color: '#94A3B8', fontSize: '13px', margin: '0 0 12px 0' }}>
                  You&apos;ll be hired at a random {state} {rulesForState(state).classification} school in the matching prestige range (or the closest one).
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  {DIFFICULTIES.map((d) => (
                    <button key={d.id} onClick={() => startNew(d.id)} disabled={busy} style={cardBtn(d.color, busy)}>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: d.color }}>{d.label}</div>
                      <div style={{ fontSize: '12px', color: '#64748B', margin: '2px 0 8px 0' }}>School prestige {prestigeRange(d.id)}</div>
                      <div style={{ fontSize: '13px', color: '#334155', lineHeight: 1.4 }}>{d.blurb}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <BackButton onClick={() => setView('MENU')} />
          </div>
        )}

        {view === 'LOAD' && (
          <div>
            <h2 style={sectionTitle}>Load Game</h2>
            {error && <p style={{ textAlign: 'center', color: '#FCA5A5' }}>{error}</p>}
            {saves === null && <p style={{ textAlign: 'center', color: '#CBD5E1' }}>Loading saves…</p>}
            {saves?.length === 0 && (
              <p style={{ textAlign: 'center', color: '#CBD5E1' }}>No saved games yet. Games autosave every week, and you can save anytime from ⚙️ Save / Load.</p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {saves?.map((save) => (
                <div key={save.id} style={saveRow}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ fontWeight: 'bold', color: '#0F172A' }}>
                      {save.teamName}
                      {save.isAutosave && <span style={badge('#FEF3C7', '#92400E')}>Autosave</span>}
                      {save.inCloud && <span style={badge('#DCFCE7', '#166534')}>{save.cloudNewer ? '☁️ Cloud' : '☁️ Synced'}</span>}
                      {save.difficulty && <span style={badge('#EEF2FF', '#3730A3')}>{save.difficulty.charAt(0) + save.difficulty.slice(1).toLowerCase()}</span>}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748B' }}>
                      {save.currentYear} season · Week {save.currentWeek} · {save.wins}-{save.losses} · {new Date(save.timestamp).toLocaleString()}
                    </div>
                    {!save.isAutosave && <div style={{ fontSize: '12px', color: '#475569' }}>{save.saveName}</div>}
                    {lockedNote(save) && (
                      <div style={{ display: 'inline-block', marginTop: '4px', fontSize: '11.5px', fontWeight: 700, color: '#92400E', background: '#FEF3C7', borderRadius: '6px', padding: '3px 7px' }}>
                        {lockedNote(save)}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {confirmDelete === save.id ? (
                      <>
                        <button onClick={() => remove(save.id)} style={smallBtn('#DC2626', '#fff')}>
                          Delete
                        </button>
                        <button onClick={() => setConfirmDelete(null)} style={smallBtn('#E2E8F0', '#334155')}>
                          Keep
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => load(save)} disabled={busy} style={smallBtn('#2563EB', '#fff')}>
                          Load
                        </button>
                        <button onClick={() => setConfirmDelete(save.id)} disabled={busy} style={smallBtn('#E2E8F0', '#B91C1C')} aria-label={`Delete save for ${save.teamName}`}>
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <BackButton onClick={() => setView('MENU')} />
          </div>
        )}
        {/* The team pages (scripts/team-pages/build.mjs writes the list): a quiet line, for fans and search engines */}
        {view === 'MENU' && TEAM_PAGES.length > 0 && (
          <p className="title-teams">
            Real high school football: Friday nights under the lights, district rivals, the playoffs and one state title on the line. Can you win state
            with{' '}
            {TEAM_PAGES.map((t, i) => (
              <React.Fragment key={t.path}>
                {i > 0 && (i === TEAM_PAGES.length - 1 ? ' or ' : ', ')}
                <a href={sitePage(t.path)} aria-label={`${t.full}, ${t.state}`} onClick={() => track('title_team_link', { state: t.state, school: t.school })}>
                  {t.short}
                </a>
              </React.Fragment>
            ))}
            ? Or take your own school there.
          </p>
        )}
        <nav className="title-links" aria-label="Policies">
          <a href={sitePage('/privacy')} target="_blank" rel="noopener">
            Privacy
          </a>{' '}
          ·{' '}
          <a href={sitePage('/terms')} target="_blank" rel="noopener">
            Terms
          </a>{' '}
          ·{' '}
          <a href={sitePage('/about')} target="_blank" rel="noopener">
            About
          </a>{' '}
          · <button onClick={reopenCookieBanner}>Cookies</button>
        </nav>
        {/* The build the coach is playing (package.json), for bug reports */}
        <div style={{ textAlign: 'center', marginTop: '8px', color: '#64748B', fontSize: '12px' }}>Version {version}</div>
      </div>
    </div>
  );
};

const BackButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <div style={{ textAlign: 'center', marginTop: '20px' }}>
    <button onClick={onClick} style={{ background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer', fontSize: '14px' }}>
      ← Back
    </button>
  </div>
);

const pageStyle: React.CSSProperties = {
  position: 'relative', // the account pill sits in its top corner
  minHeight: '100%',
  boxSizing: 'border-box',
  background: 'radial-gradient(circle at top, #1E3A5F 0%, #0F172A 60%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 16px',
  fontFamily: 'sans-serif'
};

const anyProgramBtn = (open: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  width: '100%',
  minHeight: '56px',
  borderRadius: '12px',
  border: `2px ${open ? 'solid' : 'dashed'} #60A5FA`,
  background: open ? 'rgba(37,99,235,.25)' : 'rgba(37,99,235,.12)',
  color: '#F8FAFC',
  padding: '8px 12px',
  textAlign: 'left',
  font: 'inherit',
  cursor: 'pointer'
});

const sectionTitle: React.CSSProperties = { textAlign: 'center', color: '#F8FAFC', margin: '0 0 8px 0', fontSize: '20px' };

const menuBtn = (background: string, color: string): React.CSSProperties => ({
  padding: '14px',
  background,
  color,
  border: 'none',
  borderRadius: '8px',
  fontWeight: 'bold',
  fontSize: '16px',
  cursor: 'pointer'
});

const cardBtn = (accent: string, disabled: boolean): React.CSSProperties => ({
  textAlign: 'left',
  background: '#fff',
  border: 'none',
  borderTop: `5px solid ${accent}`,
  borderRadius: '8px',
  padding: '16px',
  cursor: disabled ? 'wait' : 'pointer',
  opacity: disabled ? 0.7 : 1
});

const saveRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
  background: '#fff',
  borderRadius: '8px',
  padding: '12px 14px'
};

const badge = (background: string, color: string): React.CSSProperties => ({
  background,
  color,
  fontSize: '12px',
  fontWeight: 'bold',
  padding: '2px 6px',
  borderRadius: '4px',
  marginLeft: '8px'
});

const smallBtn = (background: string, color: string): React.CSSProperties => ({
  minHeight: '40px', // comfortable tap target
  padding: '8px 12px',
  background,
  color,
  border: 'none',
  borderRadius: '6px',
  fontWeight: 'bold',
  fontSize: '13px',
  cursor: 'pointer'
});
